// ./client/src/admin/pages/ProxySettings.js
import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  TextField,
  Button,
  Chip,
  IconButton,
  Divider,
  Alert,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  CircularProgress,
  alpha,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  LinearProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import VpnLockIcon from "@mui/icons-material/VpnLock";
import RouterIcon from "@mui/icons-material/Router";
import SpeedIcon from "@mui/icons-material/Speed";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CloseIcon from "@mui/icons-material/Close";
import { GlobalContext } from "../../context/GlobalContext";
import { TranslateContext } from "../../context/TranslateContext";
import PageHeader from "../../common/PageHeader";

const PROXY_TYPES = [
  { value: "socks5", label: "SOCKS5 (socks5://)" },
  { value: "socks5h", label: "SOCKS5H — remote DNS (socks5h://)" },
  { value: "socks4", label: "SOCKS4 (socks4://)" },
  { value: "socks4a", label: "SOCKS4A — remote DNS (socks4a://)" },
  { value: "http", label: "HTTP (http://)" },
  { value: "https", label: "HTTPS (https://)" },
];

const ProxySettings = () => {
  const { hitAxios } = React.useContext(GlobalContext);
  const { lang } = React.useContext(TranslateContext);
  const theme = useTheme();

  const [proxies, setProxies] = useState([]);
  const [useProxy, setUseProxy] = useState(false);
  const [defaultProxy, setDefaultProxy] = useState(null);
  const [addDialog, setAddDialog] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [retestingUid, setRetestingUid] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  // Add form state
  const [form, setForm] = useState({
    label: "",
    proxy_type: "socks5",
    host: "",
    port: "",
    username: "",
    password: "",
  });

  useEffect(() => {
    loadProxies();
    loadStatus();
  }, []);

  const loadProxies = async () => {
    const res = await hitAxios({
      path: "/api/proxy/get-all",
      admin: true,
      showSnackbar: false,
    });
    if (res?.data?.success) setProxies(res.data.data);
  };

  const loadStatus = async () => {
    const res = await hitAxios({
      path: "/api/proxy/status",
      admin: true,
      showSnackbar: false,
    });
    if (res?.data?.success) {
      setUseProxy(res.data.use_proxy);
      setDefaultProxy(res.data.default_proxy);
    }
  };

  // Build proxy URL from form fields
  const buildProxyUrl = () => {
    const { proxy_type, host, port, username, password } = form;
    if (!host || !port) return "";
    const auth = username
      ? `${encodeURIComponent(username)}:${encodeURIComponent(password)}@`
      : "";
    return `${proxy_type}://${auth}${host}:${port}`;
  };

  const handleTest = async () => {
    const proxyUrl = buildProxyUrl();
    if (!proxyUrl) return;
    setTesting(true);
    setTestResult(null);
    const res = await hitAxios({
      path: "/api/proxy/test",
      post: true,
      admin: true,
      obj: { proxy_url: proxyUrl },
      showSnackbar: false,
    });
    setTesting(false);
    setTestResult(res?.data || { success: false, msg: "No response" });
  };

  const handleAdd = async () => {
    const proxyUrl = buildProxyUrl();
    if (!proxyUrl) return;
    setSaving(true);
    const res = await hitAxios({
      path: "/api/proxy/add",
      post: true,
      admin: true,
      obj: {
        label:
          form.label ||
          `${form.proxy_type.toUpperCase()} — ${form.host}:${form.port}`,
        proxy_url: proxyUrl,
        proxy_type: form.proxy_type,
      },
    });
    setSaving(false);
    if (res?.data?.success) {
      setAddDialog(false);
      setForm({
        label: "",
        proxy_type: "socks5",
        host: "",
        port: "",
        username: "",
        password: "",
      });
      setTestResult(null);
      loadProxies();
    }
  };

  const handleDelete = async (uid) => {
    if (!window.confirm("Delete this proxy?")) return;
    await hitAxios({
      path: "/api/proxy/delete",
      post: true,
      admin: true,
      obj: { uid },
    });
    loadProxies();
    loadStatus();
  };

  const handleSetDefault = async (uid) => {
    await hitAxios({
      path: "/api/proxy/set-default",
      post: true,
      admin: true,
      obj: { uid },
    });
    loadProxies();
    loadStatus();
  };

  const handleRetest = async (uid) => {
    setRetestingUid(uid);
    await hitAxios({
      path: "/api/proxy/retest",
      post: true,
      admin: true,
      obj: { uid },
      showSnackbar: false,
    });
    setRetestingUid(null);
    loadProxies();
  };

  const handleToggleMode = async (checked) => {
    setUseProxy(checked);
    await hitAxios({
      path: "/api/proxy/toggle-mode",
      post: true,
      admin: true,
      obj: { use_proxy: checked ? 1 : 0 },
    });
    loadStatus();
  };

  const proxyUrl = buildProxyUrl();
  const canTest = !!(form.host && form.port);
  const canAdd = testResult?.success && canTest;

  return (
    <Box>
      <PageHeader
        title="Proxy Settings"
        subtitle="Configure SOCKS/HTTP proxy servers for torrent downloads"
        icon={VpnLockIcon}
        primaryAction={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setAddDialog(true);
              setTestResult(null);
            }}
            sx={{ borderRadius: 2 }}
          >
            Add Proxy
          </Button>
        }
      />

      {/* ── Network Mode Toggle ── */}
      <Card
        sx={{
          mb: 3,
          border: `2px solid ${
            useProxy
              ? alpha(theme.palette.primary.main, 0.4)
              : alpha(theme.palette.success.main, 0.3)
          }`,
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            height: 4,
            background: useProxy
              ? `linear-gradient(90deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.5)})`
              : `linear-gradient(90deg, ${theme.palette.success.main}, ${alpha(theme.palette.success.main, 0.5)})`,
          }}
        />
        <CardContent sx={{ p: 3 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ sm: "center" }}
            spacing={2}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: 2.5,
                  bgcolor: alpha(
                    useProxy
                      ? theme.palette.primary.main
                      : theme.palette.success.main,
                    0.1,
                  ),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {useProxy ? (
                  <VpnLockIcon sx={{ fontSize: 26, color: "primary.main" }} />
                ) : (
                  <RouterIcon sx={{ fontSize: 26, color: "success.main" }} />
                )}
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  {useProxy ? "Proxy Mode" : "VPS Network Mode"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {useProxy
                    ? `Torrents route through: ${defaultProxy ? defaultProxy.label : "⚠️ No default proxy set"}`
                    : "Torrents use your server's direct network connection"}
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={2} alignItems="center">
              <Chip
                label={useProxy ? "Proxy Active" : "Direct Connection"}
                color={useProxy ? "primary" : "success"}
                variant="outlined"
                sx={{ fontWeight: 700 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={useProxy}
                    onChange={(e) => handleToggleMode(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2" fontWeight={600}>
                    Use Proxy
                  </Typography>
                }
                labelPlacement="start"
              />
            </Stack>
          </Stack>

          {useProxy && !defaultProxy && (
            <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
              No default proxy is set. Add a proxy and mark it as default to
              activate proxy routing.
            </Alert>
          )}

          {useProxy && defaultProxy && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <CheckCircleIcon sx={{ fontSize: 18, color: "success.main" }} />
                <Typography variant="body2" fontWeight={600}>
                  Active: {defaultProxy.label}
                </Typography>
                <Chip
                  label={defaultProxy.proxy_type?.toUpperCase()}
                  size="small"
                  sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700 }}
                />
                {defaultProxy.last_test_latency > 0 && (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <SpeedIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                    <Typography variant="caption" color="text.secondary">
                      {defaultProxy.last_test_latency}ms
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* ── Proxy List ── */}
      {proxies.length === 0 ? (
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Box textAlign="center" py={6}>
              <VpnLockIcon
                sx={{
                  fontSize: 52,
                  color: alpha(theme.palette.primary.main, 0.2),
                  mb: 1.5,
                }}
              />
              <Typography
                color="text.secondary"
                variant="body2"
                fontWeight={500}
              >
                No proxy servers added yet. Add one to get started.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {proxies.map((proxy) => {
            const isDefault = proxy.is_default === 1;
            const isSuccess = proxy.last_test_status === "success";
            const isFailed = proxy.last_test_status === "failed";
            const isRetesting = retestingUid === proxy.uid;

            return (
              <Card
                key={proxy.uid}
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${
                    isDefault
                      ? alpha(theme.palette.primary.main, 0.4)
                      : theme.palette.divider
                  }`,
                  borderWidth: isDefault ? 2 : 1,
                  transition: "all 0.2s ease",
                  position: "relative",
                  overflow: "hidden",
                  "&::before": isDefault
                    ? {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 3,
                        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.4)})`,
                      }
                    : {},
                }}
              >
                {isRetesting && (
                  <LinearProgress
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 2,
                    }}
                  />
                )}
                <CardContent sx={{ p: 2.5 }}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ sm: "center" }}
                    spacing={2}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: 2,
                          bgcolor: alpha(
                            isSuccess
                              ? theme.palette.success.main
                              : isFailed
                                ? theme.palette.error.main
                                : theme.palette.warning.main,
                            0.1,
                          ),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {isSuccess ? (
                          <CheckCircleIcon
                            sx={{ fontSize: 22, color: "success.main" }}
                          />
                        ) : isFailed ? (
                          <ErrorIcon
                            sx={{ fontSize: 22, color: "error.main" }}
                          />
                        ) : (
                          <VpnLockIcon
                            sx={{ fontSize: 22, color: "warning.main" }}
                          />
                        )}
                      </Box>
                      <Box>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          flexWrap="wrap"
                        >
                          <Typography variant="subtitle1" fontWeight={700}>
                            {proxy.label}
                          </Typography>
                          {isDefault && (
                            <Chip
                              label="Default"
                              size="small"
                              color="primary"
                              sx={{
                                height: 20,
                                fontSize: "0.62rem",
                                fontWeight: 700,
                              }}
                            />
                          )}
                          <Chip
                            label={proxy.proxy_type?.toUpperCase()}
                            size="small"
                            variant="outlined"
                            sx={{
                              height: 20,
                              fontSize: "0.62rem",
                              fontWeight: 600,
                            }}
                          />
                          <Chip
                            label={
                              proxy.last_test_status === "success"
                                ? "Online"
                                : proxy.last_test_status === "failed"
                                  ? "Offline"
                                  : "Untested"
                            }
                            size="small"
                            color={
                              isSuccess
                                ? "success"
                                : isFailed
                                  ? "error"
                                  : "warning"
                            }
                            sx={{
                              height: 20,
                              fontSize: "0.62rem",
                              fontWeight: 700,
                            }}
                          />
                        </Stack>
                        <Stack
                          direction="row"
                          spacing={2}
                          mt={0.5}
                          flexWrap="wrap"
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontFamily: "monospace" }}
                          >
                            {proxy.proxy_url?.replace(/:[^:@]+@/, ":****@")}
                          </Typography>
                          {proxy.last_test_latency > 0 && (
                            <Stack
                              direction="row"
                              spacing={0.5}
                              alignItems="center"
                            >
                              <SpeedIcon
                                sx={{ fontSize: 12, color: "text.secondary" }}
                              />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {proxy.last_test_latency}ms
                              </Typography>
                            </Stack>
                          )}
                          {proxy.last_tested_at && (
                            <Typography variant="caption" color="text.disabled">
                              Tested:{" "}
                              {new Date(proxy.last_tested_at).toLocaleString()}
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                    </Stack>

                    <Stack
                      direction="row"
                      spacing={0.75}
                      alignItems="center"
                      flexShrink={0}
                    >
                      <Tooltip title="Re-test connection">
                        <IconButton
                          size="small"
                          onClick={() => handleRetest(proxy.uid)}
                          disabled={isRetesting}
                          sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1.5,
                          }}
                        >
                          {isRetesting ? (
                            <CircularProgress size={14} />
                          ) : (
                            <RefreshIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={isDefault ? "Already default" : "Set as default"}
                      >
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleSetDefault(proxy.uid)}
                            disabled={isDefault}
                            sx={{
                              border: "1px solid",
                              borderColor: isDefault
                                ? "primary.main"
                                : "divider",
                              borderRadius: 1.5,
                              color: isDefault
                                ? "primary.main"
                                : "text.secondary",
                            }}
                          >
                            {isDefault ? (
                              <StarIcon fontSize="small" />
                            ) : (
                              <StarBorderIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Delete proxy">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(proxy.uid)}
                          sx={{
                            border: "1px solid",
                            borderColor: alpha(theme.palette.error.main, 0.3),
                            borderRadius: 1.5,
                            color: alpha(theme.palette.error.main, 0.6),
                            "&:hover": {
                              color: "error.main",
                              borderColor: "error.main",
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      {/* ── Add Proxy Dialog ── */}
      <Dialog
        open={addDialog}
        onClose={() => {
          setAddDialog(false);
          setTestResult(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <VpnLockIcon sx={{ fontSize: 20, color: "primary.main" }} />
              </Box>
              <Box>
                <Typography fontWeight={700}>Add Proxy Server</Typography>
                <Typography variant="caption" color="text.secondary">
                  Connection will be tested before saving
                </Typography>
              </Box>
            </Stack>
            <IconButton
              size="small"
              onClick={() => {
                setAddDialog(false);
                setTestResult(null);
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Stack spacing={2.5} mt={1.5}>
            {/* Label */}
            <TextField
              fullWidth
              size="small"
              label="Label (optional)"
              placeholder="e.g. US Proxy 1"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
            />

            {/* Type + Host + Port */}
            <Stack direction="row" spacing={1.5}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={form.proxy_type}
                  label="Type"
                  onChange={(e) =>
                    setForm({ ...form, proxy_type: e.target.value })
                  }
                >
                  {PROXY_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Host / IP"
                placeholder="192.168.1.1 or proxy.example.com"
                value={form.host}
                onChange={(e) => setForm({ ...form, host: e.target.value })}
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                label="Port"
                placeholder="1080"
                value={form.port}
                onChange={(e) =>
                  setForm({ ...form, port: e.target.value.replace(/\D/g, "") })
                }
                sx={{ width: 90 }}
              />
            </Stack>

            {/* Auth (optional) */}
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={700}
                sx={{
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  display: "block",
                  mb: 1,
                }}
              >
                Authentication (optional)
              </Typography>
              <Stack direction="row" spacing={1.5}>
                <TextField
                  size="small"
                  label="Username"
                  value={form.username}
                  onChange={(e) =>
                    setForm({ ...form, username: e.target.value })
                  }
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={(e) =>
                    setForm({ ...form, password: e.target.value })
                  }
                  sx={{ flex: 1 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setShowPassword((v) => !v)}
                        >
                          {showPassword ? (
                            <VisibilityOffIcon sx={{ fontSize: 16 }} />
                          ) : (
                            <VisibilityIcon sx={{ fontSize: 16 }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>
            </Box>

            {/* Preview URL */}
            {proxyUrl && (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={700}
                  display="block"
                  mb={0.5}
                >
                  PROXY URL PREVIEW
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: "monospace",
                    wordBreak: "break-all",
                    color: "text.primary",
                  }}
                >
                  {proxyUrl.replace(/:[^:@]+@/, ":****@")}
                </Typography>
              </Box>
            )}

            {/* Test Result */}
            {testResult && (
              <Alert
                severity={testResult.success ? "success" : "error"}
                sx={{ borderRadius: 2 }}
                onClose={() => setTestResult(null)}
              >
                {testResult.success
                  ? `✅ Connected! Latency: ${testResult.latency}ms · Exit IP: ${testResult.ip}`
                  : `❌ ${testResult.error || testResult.msg || "Connection failed"}`}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => {
              setAddDialog(false);
              setTestResult(null);
            }}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={handleTest}
            disabled={!canTest || testing}
            startIcon={
              testing ? <CircularProgress size={14} /> : <RefreshIcon />
            }
            sx={{ borderRadius: 2 }}
          >
            {testing ? "Testing..." : "Test Connection"}
          </Button>
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={!canAdd || saving}
            startIcon={
              saving ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <AddIcon />
              )
            }
            sx={{ borderRadius: 2, fontWeight: 700 }}
          >
            {saving ? "Adding..." : "Add Proxy"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProxySettings;
